import {
    useId,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    isValidElement,
    cloneElement,
    type ReactNode,
    type RefObject,
} from 'react';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { Popover } from '@rocco/components/popover';
import { PopoverContent } from '@rocco/components/popover/popover-content';
import { PopoverTrigger } from '@rocco/components/popover/popover-trigger';
import { PopoverAnchor } from '@rocco/components/popover/popover-anchor';
import { Drawer } from '@rocco/components/drawer/drawer';
import { DrawerContent } from '@rocco/components/drawer/drawer-content';

import { FollowSelectorContent } from './follow-selector/FollowSelectorContent';
import { useFollowSelectorInstance } from '../context/FollowSelectorContext';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useGuestSave } from '~/modules/moodboards/hooks/use-guest-save';
import { isUserLoggedIn, useUser } from '@modules/user';
import type { FollowSelectorItem } from '../types/follow-selector';
import type { PinItemType } from '~/modules/moodboards/type';
import type { DesignBoardViewModel } from '@rocco/ui/design-board/view-models/design-board';
import { buildThumbnail } from '../utils/build-sections';
import { useTracker } from '@archipro-website/tracker';

interface FollowSelectorProps {
    /**
     * The trigger element that opens the popover when clicked.
     * When omitted, the component operates in headless mode — opening
     * is controlled externally and a PopoverAnchor is rendered for positioning.
     */
    trigger?: ReactNode;
    /**
     * External instance ID for coordinating with useFollowSelectorProxy.
     * When not provided, an internal ID is generated via useId().
     */
    instanceId?: string;
    /**
     * Ref forwarded to the PopoverAnchor element in headless mode.
     * Used by useFollowSelectorProxy to position the anchor at the clicked element.
     */
    anchorRef?: RefObject<HTMLSpanElement>;
    /** The item ID. */
    pinId: number;
    /** The library item ID (for products/projects/etc). If not provided, pinId is used. */
    libraryItemId?: number;
    /** The pin type. */
    pinType: PinItemType;
    /** Optional callback when follow state changes. */
    onFollowChange?: (isFollowed: boolean) => void;
    /** Optional list of items to save/remove together (e.g. palettes). */
    selectedItems?: { id: number; libraryItemId?: number }[];
    /** Horizontal alignment of the popover relative to the anchor. Defaults to 'end'. */
    popoverAlign?: 'start' | 'center' | 'end';
}

/**
 * Generic follow selector that wraps any trigger element with board selection functionality.
 * Uses Radix Popover for stable positioning and manages all follow/save logic internally.
 *
 * Supports two modes:
 * 1. **Trigger mode** (default): Pass a `trigger` element, clicks open the popover.
 * 2. **Headless mode**: Omit `trigger` and pass an `instanceId`. Opening is controlled
 *    externally via the FollowSelector context. A PopoverAnchor is rendered for positioning.
 *
 * @example
 * // Trigger mode — with a custom button
 * <FollowSelector
 *   trigger={<button>Save to Board</button>}
 *   pinId={123}
 *   pinType="ProductPin"
 * />
 *
 * @example
 * // Headless mode — controlled externally via useFollowSelectorProxy
 * <FollowSelector instanceId={id} pinId={123} pinType="ProductPin" />
 */
export const FollowSelector = ({
    trigger,
    instanceId: externalInstanceId,
    anchorRef,
    pinId,
    libraryItemId,
    pinType,
    onFollowChange,
    selectedItems,
    popoverAlign = 'end',
}: FollowSelectorProps) => {
    // Use external ID if provided, otherwise generate one internally
    const internalId = useId();
    const instanceId = externalInstanceId ?? internalId;
    const isHeadless = !trigger;
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);

    const tracker = useTracker();
    const user = useUser();
    const guestID =
        user.__typename === 'Me' ? user.TrackedGuest?.ID : undefined;
    const isLoggedIn = isUserLoggedIn(user);
    const { handleSaveToBoard, handleRemoveFromBoard } = useHandleFollow();
    const { guestSave } = useGuestSave();

    const processingBoardId = useRef<number | null>(null);

    const {
        isOpen,
        openFollowSelector,
        closeFollowSelector,
        getSections,
        isLoading,
        membershipLoading,
        hasError,
        retry,
        handleBoardCreated,
        moveToTop,
        prependBoard,
        refetchBoards,
        loadMore,
        hasNextPage,
        isLoadingMore,
        handleSearch,
        searchQuery,
        isSearching,
    } = useFollowSelectorInstance(instanceId);

    // Reset search when popover closes (only when isOpen goes from true to false)
    const prevIsOpen = useRef(isOpen);
    useEffect(() => {
        if (prevIsOpen.current && !isOpen) {
            handleSearch('');
        }
        prevIsOpen.current = isOpen;
    }, [isOpen, handleSearch]);

    // In headless mode on desktop, close the popover on scroll or resize
    // since the fixed-position anchor doesn't move with the page layout.
    // Scroll events originating from inside the popover content are ignored.
    // On mobile the Drawer handles its own dismiss logic, so we skip this.
    const popoverContentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!isHeadless || !isOpen || isMobile) return;

        const handleScroll = (e: Event) => {
            // Ignore scroll events that originate inside the popover content
            if (
                e.target instanceof Node &&
                popoverContentRef.current?.contains(e.target)
            ) {
                return;
            }

            // Also check Radix portal — the popover content may be inside a
            // portal div that sits outside the normal DOM hierarchy.
            if (
                e.target instanceof HTMLElement &&
                e.target.closest('[data-radix-popper-content-wrapper]')
            ) {
                return;
            }

            closeFollowSelector(null);
        };
        const handleResize = () => closeFollowSelector(null);

        window.addEventListener('scroll', handleScroll, { capture: true });
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('scroll', handleScroll, {
                capture: true,
            });
            window.removeEventListener('resize', handleResize);
        };
    }, [isHeadless, isOpen, isMobile, closeFollowSelector]);

    const resolvedItems = useMemo(
        () => selectedItems ?? [{ id: pinId, libraryItemId }],
        [selectedItems, pinId, libraryItemId],
    );
    const selectedItemIds = useMemo(
        () => resolvedItems.map((item) => item.libraryItemId ?? item.id),
        [resolvedItems],
    );

    const handleTriggerClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            // Guest flow: store pin and show auth modal so user can log in
            if (!isLoggedIn) {
                guestSave({
                    pinId,
                    pinType,
                    libraryItemId,
                    items: selectedItems,
                });
                return;
            }

            openFollowSelector(instanceId, {
                selectedItemIds: selectedItemIds,
                selectedItemType: pinType,
            });
        },
        [
            isLoggedIn,
            guestSave,
            pinId,
            pinType,
            libraryItemId,
            selectedItems,
            instanceId,
            openFollowSelector,
            selectedItemIds,
        ],
    );

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                closeFollowSelector(null);
            }
        },
        [closeFollowSelector],
    );

    const handleSelect = useCallback(
        async (item: FollowSelectorItem) => {
            closeFollowSelector({ item, isNewBoard: false });

            // If item is already saved, remove it from the board (toggle off)
            if (item.isSaved) {
                const success = await handleRemoveFromBoard(
                    item,
                    resolvedItems,
                    pinType,
                );

                if (success) {
                    onFollowChange?.(false);
                }
                return;
            }

            // Save to the selected board
            const success = await handleSaveToBoard(
                item,
                resolvedItems,
                pinType,
            );

            if (success) {
                moveToTop(item.id);
                onFollowChange?.(true);
                const itemId =
                    resolvedItems[0]?.libraryItemId ?? resolvedItems[0]?.id;
                if (itemId) {
                    void tracker.log('SaveSubmit', {
                        url: new URL(window.location.href),
                        data: {
                            LibraryItemID: itemId,
                            ...(guestID ? { guest_id: guestID } : {}),
                        },
                    });
                }
            }
        },
        [
            tracker,
            closeFollowSelector,
            handleSaveToBoard,
            handleRemoveFromBoard,
            resolvedItems,
            pinType,
            moveToTop,
            onFollowChange,
            guestID,
        ],
    );

    const handleBoardCreatedWrapper = useCallback(
        async (board: DesignBoardViewModel) => {
            // Prevent concurrent processing of the same board
            if (processingBoardId.current === board.id) {
                return;
            }
            processingBoardId.current = board.id;

            try {
                // Convert board to FollowSelectorItem format for saving
                const item: FollowSelectorItem = {
                    id: board.id,
                    title: board.title,
                    projectName: board.project?.title,
                    thumbnailUrls: board.pins
                        .map((pin: { image: string }) => pin.image)
                        .filter(Boolean) as string[],
                    thumbnail: buildThumbnail(board),
                    entityType: 'moodboard',
                    urlSegment: board.urlSegment,
                    projectId: board.project?.id,
                    projectUrlSegment: board.project?.urlSegment,
                    isPersonal: board.isPersonalMoodBoard,
                };

                closeFollowSelector({ item, isNewBoard: true });

                // Save the pin to the newly created board
                const success = await handleSaveToBoard(
                    item,
                    resolvedItems,
                    pinType,
                );

                if (success) {
                    // Optimistically prepend the new board so it appears
                    // immediately in the selector list.
                    prependBoard(board);
                    handleBoardCreated(board);
                    onFollowChange?.(true);

                    // Background refetch to update the board's pin thumbnails.
                    // The newly created board was prepended with an empty pins
                    // array; this fetch replaces it with server data that
                    // includes the just-saved pin image.
                    refetchBoards();
                }
            } finally {
                setTimeout(() => {
                    processingBoardId.current = null;
                }, 1000);
            }
        },
        [
            closeFollowSelector,
            handleSaveToBoard,
            resolvedItems,
            pinType,
            prependBoard,
            refetchBoards,
            handleBoardCreated,
            onFollowChange,
        ],
    );

    const sections = getSections();

    const handleClose = useCallback(() => {
        closeFollowSelector(null);
    }, [closeFollowSelector]);

    /** Shared props passed to FollowSelectorContent in all modes. */
    const contentProps = {
        sections,
        onSelect: handleSelect,
        onBoardCreated: handleBoardCreatedWrapper,
        onSearch: handleSearch,
        isLoading,
        membershipLoading,
        hasError,
        onRetry: retry,
        hasNextPage,
        onLoadMore: loadMore,
        isLoadingMore,
        searchQuery,
        isSearching,
    } as const;

    // ── Mobile: full-screen bottom drawer ──────────────────────────────
    if (isMobile) {
        // Inject click handler directly onto the trigger element via cloneElement.
        // This mirrors how Radix PopoverTrigger's asChild merges handlers on
        // desktop — handlers on the same element are not blocked by
        // stopPropagation, unlike a parent wrapper with onClickCapture.
        const mobileTrigger = !trigger ? null : isValidElement(trigger) ? (
            cloneElement(
                trigger as React.ReactElement<Record<string, unknown>>,
                { onClick: handleTriggerClick },
            )
        ) : (
            <span onClick={handleTriggerClick}>{trigger}</span>
        );

        return (
            <>
                {mobileTrigger}

                {/* Headless anchor (invisible, only used for proxy positioning) */}
                {isHeadless && (
                    <span
                        ref={anchorRef}
                        aria-hidden="true"
                        style={{
                            position: 'fixed',
                            pointerEvents: 'none',
                        }}
                    />
                )}

                <Drawer
                    open={isOpen}
                    onOpenChange={handleOpenChange}
                    direction="bottom"
                    handleOnly
                    noBodyStyles
                >
                    <DrawerContent
                        variant="default"
                        hideDefaultCloseButton
                        noHandle
                        className="h-[100dvh] !rounded-none"
                    >
                        <FollowSelectorContent
                            {...contentProps}
                            fullHeight
                            onClose={handleClose}
                        />
                    </DrawerContent>
                </Drawer>
            </>
        );
    }

    // ── Desktop: popover ───────────────────────────────────────────────
    const popoverContent = (
        <PopoverContent
            ref={isHeadless ? popoverContentRef : undefined}
            paddings="noPadding"
            background="white"
            className="w-[22.5rem] shadow-[0_0_24px_rgba(0,0,0,0.12)]"
            side="bottom"
            align={popoverAlign}
            sideOffset={8}
        >
            <FollowSelectorContent {...contentProps} />
        </PopoverContent>
    );

    if (isHeadless) {
        return (
            <Popover open={isOpen} onOpenChange={handleOpenChange}>
                <PopoverAnchor asChild>
                    <span
                        ref={anchorRef}
                        aria-hidden="true"
                        style={{
                            position: 'fixed',
                            pointerEvents: 'none',
                        }}
                    />
                </PopoverAnchor>
                {popoverContent}
            </Popover>
        );
    }

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild onClick={handleTriggerClick}>
                {trigger}
            </PopoverTrigger>
            {popoverContent}
        </Popover>
    );
};
